import { useCallback, useState } from 'react';

import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { formatSortParam } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

const PAGE_SIZE = 20;

interface RelatedRecordSectionProps {
  recordId: string;
  gridType: string;
  defaultColSettings: DataGridColSettings[];
  fetchFn: (
    id: string,
    params?: any,
  ) => Promise<{ data: { docs: any[]; totalDocs: number } }>;
  storageKey: string;
  cache: Map<string, { rows: any[]; totalCount: number }>;
}

export const RelatedRecordSection = ({
  recordId,
  gridType,
  defaultColSettings,
  fetchFn,
  storageKey,
  cache,
}: RelatedRecordSectionProps) => {
  const { user } = useUserStore();
  const { updateToast } = useToastStore();

  const fetchData = useCallback(
    async (
      page = 1,
      limit = PAGE_SIZE,
      filter?: string,
      sort?: string,
    ): Promise<{ rows: any[]; totalCount: number }> => {
      const cacheKey = `${storageKey}-${page}-${limit}-${filter ?? ''}-${
        sort ?? ''
      }`;
      const cached = cache.get(cacheKey);
      if (cached) {
        return cached;
      }

      try {
        const response = await fetchFn(recordId, {
          page,
          limit,
          filter,
          sort: formatSortParam(sort, { createdAt: -1 }),
        });
        const total = response.data.totalDocs;
        const result = { rows: response.data.docs, totalCount: total };
        cache.set(cacheKey, result);
        return result;
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
        return { rows: [], totalCount: 0 };
      }
    },
    [recordId, fetchFn, storageKey, cache, updateToast],
  );

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `${storageKey}-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return defaultColSettings;
      }

      return mergeColumnSettings(defaultColSettings, savedSettings);
    },
  );

  const columns = useDataGridColumns({
    type: gridType as any,
    columnSettings,
  });

  return (
    <DataGrid
      key={`${storageKey}-table`}
      name={`${storageKey}-table`}
      fetchData={fetchData}
      columns={columns}
      columnSettings={columnSettings}
      setColumnSettings={setColumnSettings}
      autoPageSize={false}
      initialPageSize={PAGE_SIZE}
    />
  );
};
